import type { UpdateKind } from '@fruuit/shared'
import {
  Badge,
  Callout,
  Card,
  EmptyState,
  PageHeader,
  Pagination,
  Select,
  SkeletonText,
} from '@fruuit/ui'
import { Newspaper } from 'lucide-react'
import { useState } from 'react'
import { Link } from 'react-router'
import { errorMessage } from '../../api/client'
import { formatDate, truncate } from '../../features/coverage/format'
import { UPDATE_KIND_LABEL, UPDATE_LINK_LABEL, useUpdates } from '../../features/dashboard/api'
import { useTitle } from '../../lib/useTitle'

const PAGE = 20

/** All published updates (GAP-UI-004): complete, newest first, within the caller's scope. */
export function UpdatesPage() {
  useTitle('Published updates')
  const [kind, setKind] = useState<UpdateKind | ''>('')
  const [cursors, setCursors] = useState<string[]>([])
  const cursor = cursors[cursors.length - 1] ?? null
  const updates = useUpdates({ cursor, kind: kind || undefined })
  const page = updates.data
  return (
    <>
      <PageHeader
        breadcrumbs={[{ label: 'Dashboard', href: '/app' }, { label: 'Published updates' }]}
        eyebrow="Review the change"
        title="All published updates"
        description="Every released assessment, evidence review and piece of correspondence in your programme, newest first."
      />
      <Card>
        <div className="mb-5 max-w-xs">
          <Select
            label="Kind of update"
            value={kind}
            onChange={(event) => {
              setKind(event.target.value as UpdateKind | '')
              setCursors([])
            }}
            placeholder="All updates"
            options={(Object.keys(UPDATE_KIND_LABEL) as UpdateKind[]).map((k) => ({
              value: k,
              label: UPDATE_KIND_LABEL[k],
            }))}
          />
        </div>
        {updates.error ? (
          <Callout tone="danger" role="alert">
            {errorMessage(updates.error)}
          </Callout>
        ) : !page ? (
          <SkeletonText lines={8} />
        ) : page.items.length === 0 ? (
          <EmptyState
            icon={<Newspaper />}
            title="No updates have been published yet."
            variant="plain"
          />
        ) : (
          <ol className="divide-y divide-line">
            {page.items.map((u) => (
              <li key={`${u.kind}-${u.id}`} className="py-4 first:pt-0">
                <p className="flex flex-wrap items-center gap-2 text-xs text-sage">
                  <Badge tone="outline">{UPDATE_KIND_LABEL[u.kind]}</Badge>
                  {u.kind === 'assessments' ? <span>Version {u.version}</span> : null}
                  <span>{formatDate(u.published_at)}</span>
                </p>
                <p className="mt-1 text-sm">
                  {u.issuer_id ? (
                    <Link
                      to={`/app/companies/${u.issuer_id}`}
                      className="font-semibold text-teal hover:underline"
                    >
                      {u.company}
                    </Link>
                  ) : (
                    <span className="font-semibold">{u.company}</span>
                  )}
                </p>
                <h2 className="mt-0.5 font-semibold text-ink">{u.title}</h2>
                <p className="mt-1 text-sm leading-relaxed text-ink-soft">
                  {truncate(u.summary, 400)}
                </p>
                <p className="mt-1.5 flex flex-wrap gap-x-4 text-sm">
                  <Link
                    to={
                      u.kind === 'assessments' || !u.engagement_id
                        ? `/app/companies/${u.issuer_id ?? ''}`
                        : `/app/cases/${u.engagement_id}`
                    }
                    className="font-semibold text-teal hover:underline"
                  >
                    {UPDATE_LINK_LABEL[u.kind]}
                  </Link>
                  {u.engagement_id ? (
                    <Link
                      to={`/app/cases/${u.engagement_id}`}
                      className="text-teal hover:underline"
                    >
                      Engagement plan →
                    </Link>
                  ) : null}
                </p>
              </li>
            ))}
          </ol>
        )}
        {page ? (
          <Pagination
            className="mt-4"
            hasPrevious={cursors.length > 0}
            hasNext={Boolean(page.next_cursor)}
            onPrevious={() => setCursors((list) => list.slice(0, -1))}
            onNext={() =>
              page.next_cursor && setCursors((list) => [...list, page.next_cursor as string])
            }
            summary={`Page ${cursors.length + 1} · ${PAGE} per page`}
            busy={updates.isFetching}
          />
        ) : null}
      </Card>
    </>
  )
}
